import type React from 'react';
import { useLayoutEffect, useState } from 'react';

type TFittingChipsInput = {
  chipWidths: number[];
  availableWidth: number;
  // Width of the "+N more" button that has to fit after the chips when some are hidden.
  moreButtonWidth: number;
  gap: number;
};

/**
 * How many chips fit on one line. When they do not all fit, room is kept for the
 * "+N more" button. At least one chip is always shown - it truncates if it is too wide.
 */
export const countFittingChips = ({
  chipWidths,
  availableWidth,
  moreButtonWidth,
  gap,
}: TFittingChipsInput): number => {
  const totalWidth =
    chipWidths.reduce((sum, chipWidth) => sum + chipWidth, 0) +
    gap * Math.max(0, chipWidths.length - 1);

  if (totalWidth <= availableWidth) {
    return chipWidths.length;
  }

  const widthForChips = availableWidth - moreButtonWidth - gap;
  let usedWidth = 0;
  let fittingCount = 0;

  for (const chipWidth of chipWidths) {
    const nextUsedWidth = usedWidth + (fittingCount > 0 ? gap : 0) + chipWidth;

    if (nextUsedWidth > widthForChips) {
      break;
    }

    usedWidth = nextUsedWidth;
    fittingCount += 1;
  }

  return Math.max(1, fittingCount);
};

type TFittingChipsWithPinInput = TFittingChipsInput & {
  // A chip that must stay visible even when it falls past the fitting ones.
  pinnedChipIndex: number | null;
};

/**
 * Like countFittingChips, but a pinned chip that would not show gets its own room
 * first: the result is then how many of the chips before it fit next to it and
 * the "+N more" button, and the caller shows the pinned chip after them.
 */
export const countFittingChipsWithPin = ({
  chipWidths,
  availableWidth,
  moreButtonWidth,
  gap,
  pinnedChipIndex,
}: TFittingChipsWithPinInput): number => {
  const fittingCount = countFittingChips({
    chipWidths,
    availableWidth,
    moreButtonWidth,
    gap,
  });

  if (pinnedChipIndex === null || pinnedChipIndex < fittingCount) {
    return fittingCount;
  }

  const pinnedWidth = chipWidths[pinnedChipIndex] ?? 0;
  const widthForLeadingChips =
    availableWidth - pinnedWidth - gap - moreButtonWidth - gap;
  let usedWidth = 0;
  let leadingCount = 0;

  for (const chipWidth of chipWidths.slice(0, pinnedChipIndex)) {
    const nextUsedWidth = usedWidth + (leadingCount > 0 ? gap : 0) + chipWidth;

    if (nextUsedWidth > widthForLeadingChips) {
      break;
    }

    usedWidth = nextUsedWidth;
    leadingCount += 1;
  }

  return Math.max(1, leadingCount);
};

type TUseFittingChipCountInput = {
  // The visible chip row - its content width is the space the chips fit in.
  rowRef: React.RefObject<HTMLElement | null>;
  // An invisible copy of the full row holding every chip tagged
  // data-measure="chip" and a "+N more" sample tagged data-measure="more".
  measureRowRef: React.RefObject<HTMLElement | null>;
  chipCount: number;
  // Re-measures when this changes, e.g. the list the chips are built from.
  measureKey: unknown;
  // See countFittingChipsWithPin.
  pinnedChipIndex?: number | null;
};

/**
 * How many chips of a single-line chip row fit before a "+N more" button.
 * Chip widths are read from an invisible copy of the full row, so the count can be
 * recomputed on every resize without the visible row first showing every chip.
 */
export const useFittingChipCount = ({
  rowRef,
  measureRowRef,
  chipCount,
  measureKey,
  pinnedChipIndex = null,
}: TUseFittingChipCountInput): number => {
  const [fittingChipCount, setFittingChipCount] = useState(chipCount);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measureRow = measureRowRef.current;

    if (!row || !measureRow) {
      return;
    }

    const updateFittingChipCount = () => {
      const chipWidths = [
        ...measureRow.querySelectorAll<HTMLElement>('[data-measure="chip"]'),
      ].map((chip) => chip.offsetWidth);
      const moreButton = measureRow.querySelector<HTMLElement>(
        '[data-measure="more"]',
      );

      const rowStyle = getComputedStyle(row);
      const rowPadding =
        (Number.parseFloat(rowStyle.paddingLeft) || 0) +
        (Number.parseFloat(rowStyle.paddingRight) || 0);

      setFittingChipCount(
        countFittingChipsWithPin({
          chipWidths,
          availableWidth: row.clientWidth - rowPadding,
          moreButtonWidth: moreButton?.offsetWidth ?? 0,
          gap: Number.parseFloat(getComputedStyle(measureRow).columnGap) || 0,
          pinnedChipIndex,
        }),
      );
    };

    updateFittingChipCount();
    const resizeObserver = new ResizeObserver(updateFittingChipCount);
    resizeObserver.observe(row);
    // * The measuring row is `w-max`, so it resizes whenever a chip does - a
    // * language switch or a late web font - even when the row itself does not.
    resizeObserver.observe(measureRow);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureKey, pinnedChipIndex]);

  return fittingChipCount;
};
